import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFilter } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import CommonTable from '../../../../common/table/ComonTable';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import SearchBar from '../../../../common/formfields/SearchBar';
import { toast } from 'react-toastify';
import DeleteModal from '../../../../common/commonmodal/DeleteModal';

const ParkingSlotList = () => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [parkingSlots, setParkingSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [pageInfo, setPageInfo] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalPages: 1,
        totalRecords: 0
    });

    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);

    const fetchParkingSlots = async () => {
        setLoading(true);
        try {
            const response = await apiServices({
                method: "GET",
                url: `/ParkingSlotMaster/getallParkingSlotId`
            });

            if (response.isSuccess || response.success || response.IsSuccess) {
                let apiData = response.data;
                let data = [];
                if (Array.isArray(apiData)) {
                    data = apiData;
                } else if (apiData && Array.isArray(apiData.data)) {
                    data = apiData.data;
                } else if (apiData && Array.isArray(apiData.ParkingSlotData)) {
                    data = apiData.ParkingSlotData;
                }
                // Sort by ID descending as per Angular
                data.sort((a, b) => b.parkingSlotId - a.parkingSlotId);

                setParkingSlots(data);
                setPageInfo(prev => ({
                    ...prev,
                    totalRecords: data.length,
                    totalPages: Math.ceil(data.length / prev.pageSize)
                }));
            }
        } catch (error) {
            console.error("Error fetching parking slots:", error);
            toast.error("Failed to fetch parking slots.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchParkingSlots();
    }, []);

    const handleLimitChange = (newLimit) => {
        setPageInfo(prev => ({ ...prev, pageSize: newLimit, pageNumber: 1 }));
        setCurrentPage(1);
    };

    const onPageChange = (page) => {
        setCurrentPage(page);
    };

    const tableHeaders = [
        { label: "NO", key: "no" },
        { label: "SITE", key: "siteName" },
        { label: "WING", key: "wingName" },
        { label: "FLOOR", key: "floorName" },
        { label: "VEHICLE TYPE", key: "vehicleType" },
        { label: "PARKING TYPE", key: "parkingType" },
        { label: "SLOT NAME", key: "slotName" },
        { label: "STATUS", key: "isActive" },
        { label: "ACTIONS", key: "actions" }
    ];

    const renderCell = (key, row) => {
        switch (key) {
            case "no":
            case "slotName":
                return <span className="text-xs font-semibold text-[#1E293B]">{row[key] || "—"}</span>;
            case "siteName":
            case "wingName":
            case "floorName":
            case "vehicleType":
            case "parkingType":
                return <span className="text-xs font-medium text-slate-700">{row[key] || "—"}</span>;
            case "isActive":
                let statusColor = "bg-gray-100 text-gray-600";
                if (row.isActive === 'Active') statusColor = "bg-[#DCFCE7] text-[#166534]";
                if (row.isActive === 'Inactive') statusColor = "bg-[#FEF9C3] text-[#D97706]";
                return (
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black shadow-sm uppercase ${statusColor}`}>
                        {row.isActive || "—"}
                    </span>
                );
            case "actions":
                return null;
            default:
                return <span className="text-xs font-normal text-[#64748B]">{row[key] || "—"}</span>;
        }
    };


    // Filter by search query and status tab
    const safeData = Array.isArray(parkingSlots) ? parkingSlots : [];
    const filteredData = safeData.filter(item => {
        let matchesSearch = true;
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            matchesSearch = (
                (item.siteName || "").toLowerCase().includes(query) ||
                (item.slotName || "").toLowerCase().includes(query) ||
                (item.wingName || "").toLowerCase().includes(query) ||
                (item.floorName || "").toLowerCase().includes(query)
            );
        }
        
        let matchesStatus = true;
        if (statusFilter) {
            matchesStatus = item.isActive === statusFilter;
        }

        return matchesSearch && matchesStatus;
    });

    const paginatedData = filteredData.slice((currentPage - 1) * pageInfo.pageSize, currentPage * pageInfo.pageSize).map((item, idx) => ({
        ...item,
        id: item.parkingSlotId || `ps-${idx}`,
        no: (currentPage - 1) * pageInfo.pageSize + idx + 1
    }));

    const headerAction = (
        <div className="flex flex-col sm:flex-row items-center gap-3">
            <SearchBar
                onSearch={setSearchQuery}
                placeholder="Search slot..."
                className="w-full sm:w-48"
            />
            <select
                value={statusFilter}
                onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                }}
                className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-indigo-500 focus:border-indigo-500 text-gray-700 bg-white"
            >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
            </select>
        </div>
    );

    const handleEdit = (row) => {
        navigate('/zentcomm/parking-management/parking-slot/form', { state: { recordData: row } });
    };

    const handleDeleteClick = (row) => {
        setRecordToDelete(row);
        setIsDeleteOpen(true);
    };

    const confirmDelete = async () => {
        if (!recordToDelete) return;
        try {
            const res = await apiServices({
                method: "DELETE",
                url: `api/ParkingSlotMaster/DeleteParkingSlotType/${recordToDelete.parkingSlotId}`
            });
            if (res.isSuccess || res.success || res.IsSuccess) {
                toast.success("Parking slot deleted successfully");
                fetchParkingSlots();
            } else {
                toast.error("Failed to delete parking slot");
            }
        } catch (error) {
            toast.error("Error deleting parking slot");
        } finally {
            setIsDeleteOpen(false);
            setRecordToDelete(null);
        }
    };

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <PageHeadingSection
                Navbar={true}
                mainHeading="Parking Slot List"
                subHeading="Manage the permanent parking slots"
                primaryBtnText="Add New Slot"
                primaryBtnIcon={faPlus}
                PrimaryOnClick={() => {
                    navigate('/zentcomm/parking-management/parking-slot/form');
                }}
            />

            <CommonTable
                tableHeading="Parking Slots"
                headerAction={headerAction}
                data={paginatedData}
                headers={tableHeaders}
                renderCell={renderCell}
                pagination={{
                    ...pageInfo,
                    pageNumber: currentPage,
                    totalRecords: filteredData.length,
                    totalPages: Math.ceil(filteredData.length / pageInfo.pageSize) || 1
                }}
                onPageChange={onPageChange}
                onLimitChange={handleLimitChange}
                loading={loading}
                showActionCol={true}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
            />

            {/* Delete Confirmation Modal */}
            <DeleteModal
                isOpen={isDeleteOpen}
                onClose={() => setIsDeleteOpen(false)}
                onDelete={confirmDelete}
                title="Delete Parking Slot"
                message={`Are you sure you want to delete ${recordToDelete?.slotName} slot? This action cannot be undone.`}
            />
        </div>
    );
};

export default ParkingSlotList;
